import { PALETTE_SIZE, TRAIL_OPACITY_MIN_PERCENT } from './game-engine.js';
import {
  PLAYER_TURN_RADIUS_MAX_PERCENT,
  PLAYER_TURN_RADIUS_MIN_PERCENT
} from './game-balance.js';
const PROTOCOL_VERSION = 1;
const INPUT_SEQ_MOD = 65536;
const INPUT_SEQ_HALF_RANGE = 32768;

const MAX_ROOM_ID_LEN = 64;
const MAX_PLAYER_NAME_LEN = 32;
const MAX_ROOM_PASSWORD_LEN = 64;
const MAX_CHAT_TEXT_LEN = 180;
// Covers the whole palette (solids + mesh); -1 means "back to auto-assign".
// Derived, never a literal: as a hardcoded 8 this silently rejected every mesh
// pick the moment the palette grew past the 9 solids it was written for.
const MAX_PLAYER_COLOR_ID = PALETTE_SIZE - 1;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function clampInt(value, min, max) {
  if (!Number.isFinite(Number(value))) return null;
  const parsed = Math.trunc(Number(value));
  if (parsed < min || parsed > max) return null;
  return parsed;
}

function clampOptionalNumber(value, min, max) {
  if (value === null || value === undefined || value === '') return null;
  if (!Number.isFinite(Number(value))) return null;
  const parsed = Number(value);
  if (parsed < min || parsed > max) return null;
  return parsed;
}

function clampOptionalInt(value, min, max) {
  if (value === null || value === undefined || value === '') return null;
  if (!Number.isFinite(Number(value))) return null;
  const parsed = Math.trunc(Number(value));
  if (parsed < min || parsed > max) return null;
  return parsed;
}

function clampString(value, maxLen) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLen) return null;
  return trimmed;
}

function normalizeSeq(seq) {
  return ((Number(seq) % INPUT_SEQ_MOD) + INPUT_SEQ_MOD) % INPUT_SEQ_MOD;
}

function seqDiff(a, b) {
  return (normalizeSeq(a) - normalizeSeq(b) + INPUT_SEQ_MOD) % INPUT_SEQ_MOD;
}

function isSeqNewer(a, b) {
  const diff = seqDiff(a, b);
  return diff > 0 && diff < INPUT_SEQ_HALF_RANGE;
}

function isSeqAcked(seq, ackSeq) {
  const diff = seqDiff(ackSeq, seq);
  return diff === 0 || diff < INPUT_SEQ_HALF_RANGE;
}

const SESSION_TOKEN_PATTERN = /^[0-9a-f]{32}$/;

function validateHello(message) {
  const room = clampString(message.room, MAX_ROOM_ID_LEN);
  const name = clampString(message.name, MAX_PLAYER_NAME_LEN);
  const version = clampInt(message.v, PROTOCOL_VERSION, PROTOCOL_VERSION);
  if (!room || !name || version !== PROTOCOL_VERSION) {
    return { ok: false, reason: 'invalid hello message' };
  }
  // Optional session token: silently dropped when malformed so an old or
  // corrupted stored token degrades to a fresh join instead of a reject.
  const token = typeof message.token === 'string' && SESSION_TOKEN_PATTERN.test(message.token)
    ? message.token
    : null;
  // Optional room password, carried only on the join that has to prove itself.
  // Dropped rather than rejected when absent, empty or over-long: the join then
  // presents no password at all, which the server answers with the same
  // "locked" reply as a wrong one. This value must never be echoed anywhere --
  // not into a log line, not into an error reason.
  const password = clampString(message.password, MAX_ROOM_PASSWORD_LEN);
  return {
    ok: true,
    message: {
      t: 'hello',
      v: PROTOCOL_VERSION,
      room,
      name,
      ...(token ? { token } : {}),
      ...(password ? { password } : {})
    }
  };
}

function validateBye() {
  return { ok: true, message: { t: 'bye' } };
}

function validateInput(message) {
  const seq = clampInt(message.seq, 0, INPUT_SEQ_MOD - 1);
  const clientTime = clampInt(message.clientTime, 0, Number.MAX_SAFE_INTEGER);
  const lastSnapTick = clampInt(message.lastSnapTick, 0, Number.MAX_SAFE_INTEGER);
  const applyServerMs = clampOptionalInt(message.applyServerMs, 0, Number.MAX_SAFE_INTEGER);
  const left = message.left === 1 ? 1 : message.left === 0 ? 0 : null;
  const right = message.right === 1 ? 1 : message.right === 0 ? 0 : null;
  if (seq === null || clientTime === null || lastSnapTick === null || left === null || right === null) {
    return { ok: false, reason: 'invalid input message' };
  }
  return {
    ok: true,
    message: {
      t: 'i',
      seq,
      clientTime,
      left,
      right,
      lastSnapTick,
      ...(applyServerMs !== null ? { applyServerMs } : {}),
    }
  };
}

function validatePing(message) {
  const id = clampInt(message.id, 0, Number.MAX_SAFE_INTEGER);
  const clientTime = clampInt(message.clientTime, 0, Number.MAX_SAFE_INTEGER);
  if (id === null || clientTime === null) {
    return { ok: false, reason: 'invalid ping message' };
  }
  return {
    ok: true,
    message: {
      t: 'p',
      id,
      clientTime
    }
  };
}

function validateReset() {
  return { ok: true, message: { t: 'r' } };
}

function validateTogglePause() {
  return { ok: true, message: { t: 'u' } };
}

function validateChat(message) {
  const text = clampString(message.text, MAX_CHAT_TEXT_LEN);
  if (!text) {
    return { ok: false, reason: 'invalid chat message' };
  }
  return {
    ok: true,
    message: {
      t: 'c',
      text
    }
  };
}

// Picking a side. The only thing on the wire is which side you want; whether
// there is a seat for you on it is the server's to answer.
function validateSetTeam(message) {
  if (message.team !== 'fire' && message.team !== 'water') {
    return { ok: false, reason: 'invalid set team message' };
  }
  return { ok: true, message: { t: 'st', team: message.team } };
}

function validateSetColor(message) {
  const colorId = clampInt(message.colorId, -1, MAX_PLAYER_COLOR_ID);
  if (colorId === null) {
    return { ok: false, reason: 'invalid set color message' };
  }
  // Optional: host recolors one of their manual bots instead of themselves.
  let botIndex;
  if (message.botIndex !== undefined) {
    botIndex = clampInt(message.botIndex, 0, 7);
    if (botIndex === null) {
      return { ok: false, reason: 'invalid set color message' };
    }
  }
  return {
    ok: true,
    message: {
      t: 'sc',
      colorId,
      ...(botIndex !== undefined ? { botIndex } : {})
    }
  };
}

function validateSetTrailOpacity(message) {
  const trailOpacityPercent = clampInt(message.trailOpacityPercent, TRAIL_OPACITY_MIN_PERCENT, 100);
  if (trailOpacityPercent === null) {
    return { ok: false, reason: 'invalid set trail opacity message' };
  }
  // Optional: the host styles one of their manual bots instead of themselves.
  let botIndex;
  if (message.botIndex !== undefined) {
    botIndex = clampInt(message.botIndex, 0, 7);
    if (botIndex === null) {
      return { ok: false, reason: 'invalid set trail opacity message' };
    }
  }
  return {
    ok: true,
    message: {
      t: 'so',
      trailOpacityPercent,
      ...(botIndex !== undefined ? { botIndex } : {})
    }
  };
}

function validateSetTurnRadius(message) {
  const turnRadiusPercent = clampInt(
    message.turnRadiusPercent,
    PLAYER_TURN_RADIUS_MIN_PERCENT,
    PLAYER_TURN_RADIUS_MAX_PERCENT
  );
  if (turnRadiusPercent === null) {
    return { ok: false, reason: 'invalid set turn radius message' };
  }
  return { ok: true, message: { t: 'tr', turnRadiusPercent } };
}

function validateCancelCountdown() {
  return { ok: true, message: { t: 'cc' } };
}

function validateStopMatch() {
  return { ok: true, message: { t: 'sm' } };
}

function validateSpectate(message) {
  const spectate = message.spectate === 1 ? 1 : message.spectate === 0 ? 0 : null;
  if (spectate === null) {
    return { ok: false, reason: 'invalid spectate message' };
  }
  return {
    ok: true,
    message: {
      t: 'sp',
      spectate
    }
  };
}

function validateChatDelete(message) {
  const id = clampString(message.id, 80);
  if (!id) {
    return { ok: false, reason: 'invalid chat delete message' };
  }
  return {
    ok: true,
    message: {
      t: 'cd',
      id
    }
  };
}

function validateClientMessage(message) {
  if (!isPlainObject(message) || typeof message.t !== 'string') {
    return { ok: false, reason: 'message must be an object with type' };
  }

  if (message.t === 'hello') return validateHello(message);
  if (message.t === 'i') return validateInput(message);
  if (message.t === 'p') return validatePing(message);
  if (message.t === 'r') return validateReset();
  if (message.t === 'u') return validateTogglePause();
  if (message.t === 'c') return validateChat(message);
  if (message.t === 'cd') return validateChatDelete(message);
  if (message.t === 'sc') return validateSetColor(message);
  if (message.t === 'so') return validateSetTrailOpacity(message);
  if (message.t === 'tr') return validateSetTurnRadius(message);
  if (message.t === 'st') return validateSetTeam(message);
  if (message.t === 'sp') return validateSpectate(message);
  if (message.t === 'cc') return validateCancelCountdown();
  if (message.t === 'sm') return validateStopMatch();
  if (message.t === 'bye') return validateBye();

  return { ok: false, reason: `unknown message type: ${message.t}` };
}

function parseCompactInput(parts) {
  if (parts.length < 6 || parts.length > 8) {
    return { ok: false, reason: 'invalid compact input frame' };
  }

  const seq = clampInt(parts[1], 0, INPUT_SEQ_MOD - 1);
  const clientTime = clampInt(parts[2], 0, Number.MAX_SAFE_INTEGER);
  const left = parts[3] === '1' ? 1 : parts[3] === '0' ? 0 : null;
  const right = parts[4] === '1' ? 1 : parts[4] === '0' ? 0 : null;
  const lastSnapTick = clampInt(parts[5], 0, Number.MAX_SAFE_INTEGER);
  if (seq === null || clientTime === null || left === null || right === null || lastSnapTick === null) {
    return { ok: false, reason: 'invalid compact input frame' };
  }

  // Parts 7-8 were rtt/jitter telemetry; older clients may still send them,
  // so the length window stays tolerant while the values are ignored.
  return {
    ok: true,
    message: {
      t: 'i',
      seq,
      clientTime,
      left,
      right,
      lastSnapTick
    }
  };
}

function parseCompactPing(parts) {
  if (parts.length < 3 || parts.length > 5) {
    return { ok: false, reason: 'invalid compact ping frame' };
  }

  const id = clampInt(parts[1], 0, Number.MAX_SAFE_INTEGER);
  const clientTime = clampInt(parts[2], 0, Number.MAX_SAFE_INTEGER);
  if (id === null || clientTime === null) {
    return { ok: false, reason: 'invalid compact ping frame' };
  }

  // Parts 4-5 were rtt/jitter telemetry; older clients may still send them,
  // so the length window stays tolerant while the values are ignored.
  return {
    ok: true,
    message: {
      t: 'p',
      id,
      clientTime
    }
  };
}

function parseClientWireMessage(raw) {
  if (typeof raw !== 'string') {
    return { ok: false, reason: 'binary frames are unsupported in protocol v1' };
  }

  if (raw.startsWith('i,')) {
    return parseCompactInput(raw.split(','));
  }
  if (raw.startsWith('p,')) {
    return parseCompactPing(raw.split(','));
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return { ok: false, reason: 'invalid JSON payload' };
  }
  return validateClientMessage(parsed);
}

function encodeWireMessage(message) {
  return JSON.stringify(message);
}

function makeWelcomeMessage(payload) {
  return {
    t: 'w',
    v: PROTOCOL_VERSION,
    playerId: payload.playerId,
    tickMs: payload.tickMs,
    snapshotRateHz: payload.snapshotRateHz,
    serverNow: payload.serverNow,
    simTick: payload.simTick,
    config: payload.config,
    room: payload.room,
    ...(payload.playerSettings && typeof payload.playerSettings === 'object'
      ? { playerSettings: payload.playerSettings }
      : {}),
    ...(payload.name ? { name: payload.name } : {}),
    ...(payload.token ? { token: payload.token } : {}),
    ...(payload.resume !== undefined ? { resume: Boolean(payload.resume) } : {}),
    ...(payload.chatAdmin ? { chatAdmin: true } : {})
  };
}

function makeSnapshotMessage(payload) {
  return {
    t: 's',
    simTick: payload.simTick,
    serverNow: payload.serverNow,
    ackInputSeq: payload.ackInputSeq,
    state: payload.state,
    ops: payload.ops
  };
}

function makeInputAckMessage(payload) {
  return {
    t: 'a',
    ackInputSeq: payload.ackInputSeq,
    serverNow: payload.serverNow
  };
}

function makeEventMessage(kind, data = {}) {
  return {
    t: 'e',
    kind,
    data
  };
}

function makePongMessage(payload) {
  return {
    t: 'q',
    id: payload.id,
    serverTime: payload.serverTime
  };
}

export {
  INPUT_SEQ_HALF_RANGE,
  INPUT_SEQ_MOD,
  MAX_ROOM_PASSWORD_LEN,
  PROTOCOL_VERSION,
  encodeWireMessage,
  isSeqAcked,
  isSeqNewer,
  makeInputAckMessage,
  makeEventMessage,
  makePongMessage,
  makeSnapshotMessage,
  makeWelcomeMessage,
  normalizeSeq,
  parseClientWireMessage,
  validateClientMessage
};
